import {
  addNwsAndWildfiresLayers,
  updateNwsAndWildfiresFilters,
  removeNwsAndWildfiresLayers,
  setNwsAndWildfiresLayersVisibility,
  // checkNwsAndWildfiresLayersVisible,
} from './configureNwsAndWildfiresLayers.js';
import {
  addCustomEventsLayers,
  removeCustomEventsLayers,
  setCustomEventsLayersVisibility,
} from './configureCustomEventsLayers.js';
import {
  addPowerOutageLayers,
  removePowerOutageLayers,
  setPowerOutageLayersVisibility,
} from './configurePowerOutageLayers.js';
import {
  addHurricaneLayers,
  updateHurricaneFilters,
  removeHurricaneLayers,
  setHurricaneLayersVisibility,
} from './configureHurricaneLayers.js';
import {
  addEarthquakeLayers,
  updateEarthquakeFilters,
  removeEarthquakeLayers,
  setEarthquakeLayersVisibility,
} from './configureEarthquakeLayers.js';
import {
  addMapEventHandlersMemo,
  addVectorSourceMemo,
  checkLayerExistsMemo,
  checkLayerToggleMemo,
  removeLayerMemo,
  removeSourceMemo,
  setFilterMemo,
  setLayerVisibilityMemo,
} from './mapEventsEffectMemos.js';
import {
  addSilurianProjectionLayers,
  removeSilurianProjectionLayers,
  setSilurianProjectionLayersVisibility,
} from './configureSilurianProjectionLayers.js';
import {
  MapMemo,
  LayerClickHandler,
  EarthquakeFeature,
  SilurianProjectionResponse,
} from './mapEventsEffectTypes.js';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const LAYER_REMOVAL_METHODS = [
  removeCustomEventsLayers,
  removeEarthquakeLayers,
  removeHurricaneLayers,
  removeNwsAndWildfiresLayers,
  removePowerOutageLayers,
  removeSilurianProjectionLayers,
];

export const LAYER_VISIBILITY_SETTERS = [
  setCustomEventsLayersVisibility,
  setEarthquakeLayersVisibility,
  setHurricaneLayersVisibility,
  setNwsAndWildfiresLayersVisibility,
  setPowerOutageLayersVisibility,
];

export type MapEventsEffectInput = {
  map: MapMemo;
  mapHasLoaded: boolean;
  eventsVisibleToggle: boolean;
  selectedDatetime: dayjs.Dayjs;
  setLayerClicked: LayerClickHandler;
  earthquakesData: EarthquakeFeature[];
  poweroutageActive: boolean;
  eventToggles: any[];
  group_guid: UUID;
  silurianProjectionData: SilurianProjectionResponse[];
  silurianProjectionVisibleToggle: boolean;
};

export const mapEventsEffect = ({
  map,
  mapHasLoaded,
  eventsVisibleToggle,
  selectedDatetime,
  setLayerClicked,
  earthquakesData,
  poweroutageActive,
  eventToggles,
  group_guid,
  silurianProjectionData,
  silurianProjectionVisibleToggle,
}: MapEventsEffectInput) => {

  if (!mapHasLoaded || !earthquakesData) return;

  const checkLayerToggle = checkLayerToggleMemo(eventToggles);

  const layerExists = checkLayerExistsMemo(map);

  const setFilter = setFilterMemo(map);

  const addMapEventHandlers = addMapEventHandlersMemo(map, setLayerClicked);

  const addVectorSource = addVectorSourceMemo(map);

  const setLayerVisibility = setLayerVisibilityMemo(map);

  try {
    const formattedDatetime = selectedDatetime
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');
    const endDatetime = selectedDatetime.utc().format('YYYY-MM-DD HH:mm:ss');
    const formattedHoursDatetime = selectedDatetime
      .minute(0)
      .second(0)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    // NWS Alerts and Wildfires
    addNwsAndWildfiresLayers({
      addMapEventHandlers,
      addVectorSource,
      checkLayerToggle,
      endDatetime,
      formattedDatetime,
    });

    // Custom Events
    // Add custom polygon events tiles
    addCustomEventsLayers({
      addMapEventHandlers,
      addVectorSource,
      group_guid,
    });

    // Power Outage Layers
    if (poweroutageActive) {
      addPowerOutageLayers({
        addMapEventHandlers,
        addVectorSource,
        checkLayerToggle,
        formattedHoursDatetime,
      });
    }

    // Hurricane Layers
    addHurricaneLayers({
      addMapEventHandlers,
      addVectorSource,
      checkLayerToggle,
      endDatetime,
      formattedDatetime,
    });

    if (silurianProjectionVisibleToggle) {
      // Silurian Projection Layers
      addSilurianProjectionLayers({
        addMapEventHandlers,
        silurianProjectionData: silurianProjectionData || [],
        map,
        checkLayerToggle,
      });
    }

    // Earthquakes Layers
    // note that earthquakes are configured differently due to the clustering mechanic
    addEarthquakeLayers({
      checkLayerToggle,
      earthquakesData,
      map,
      setLayerClicked,
    });
  } catch (e) {
    console.error('Error in mapEventsEffect:', e);
    return;
  }

  // Update layer visibility
  LAYER_VISIBILITY_SETTERS.forEach((setLayerVisibilityMethod) => {
    setLayerVisibilityMethod({
      setLayerVisibility,
      isVisible: eventsVisibleToggle,
    });
  });

  // this one is special because it has multiple toggles
  setSilurianProjectionLayersVisibility({
    setLayerVisibility,
    isVisible: eventsVisibleToggle 
      && silurianProjectionVisibleToggle
      && silurianProjectionData?.length > 0,
  })

  // Update NWS and Wildfires filters
  updateNwsAndWildfiresFilters(setFilter, eventToggles);

  // Update earthquake filters
  updateEarthquakeFilters(checkLayerToggle, setFilter);

  // Update hurricane filters
  updateHurricaneFilters(layerExists, checkLayerToggle, setFilter);
};

export const mapEventsEffectCleanup = ({ map }: { map: MapMemo }) => {
  if (!map.current) {
    return;
  }

  const removeSource = removeSourceMemo(map);

  const removeLayer = removeLayerMemo(map);

  // Remove all layers and sources
  LAYER_REMOVAL_METHODS.forEach((removeLayerMethod) => {
    removeLayerMethod({ removeLayer, removeSource });
  });
};
